import {Button} from 'antd';
import {FormInstance} from 'antd/es/form';
import {AxiosResponse} from 'axios';
import {omitBy} from 'lodash-es';
import React, {Dispatch, SetStateAction, FC} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {createActionGetFile} from 'modules/data/data-actions';
import {selectEntityData, selectTableEntityData} from 'modules/data/data-selectors';
import {ITableEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {ERequestType} from 'shared/types/requests';
import {performRequest, showMessageFromResponse} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {TAppState} from 'store/config/types';
import {closeModal} from 'store/slices/modals-slice';

import {DynamicIcon} from '../../../dynamic-icon';
import {TIconType} from '../../../dynamic-icon/dynamic-icon';

interface IButtonDownloadDocumentFileProps {
    actionTitle: string;
    referenceUrl: string;
    requestType: ERequestType;
    actionIcon?: TIconType;
    form?: FormInstance;
    onClose?: () => void;
    setFormErrorMessage?: Dispatch<SetStateAction<string | null>>;
    linkedEntityName?: string;
    mainReferenceUrl?: string;
    parentEntityName?: string;
}

interface IButtonDownloadFileProps extends IButtonDownloadDocumentFileProps {
}

export const ButtonDownloadDocumentFile: FC<IButtonDownloadFileProps> = ({
    actionTitle,
    referenceUrl,
    requestType,
    actionIcon,
    form,
    onClose,
    setFormErrorMessage,
    linkedEntityName,
    mainReferenceUrl,
    parentEntityName,
    ...props
}: IButtonDownloadFileProps) => {
    const dispatch = useDispatch();

    const {selectedRowKeys = []} = useSelector((state: TAppState) => (
        selectEntityData(parentEntityName || '',
            EEntityType.TABLE)(state) || {}
    ) as ITableEntityData);
    const docId = selectedRowKeys[0];

    const rows = useAppSelector(selectTableEntityData(parentEntityName || ''))?.rows;
    const sectionCodeContext: string = String(rows?.slice(0, 1).shift()?.sectionCode);

    const handleClick = async () => {
        if (!form || !onClose) {
            return;
        }
        try {
            if (mainReferenceUrl) {
                const response = await performRequest({
                    url: mainReferenceUrl,
                    method: 'GET',
                    params: {sectionCodeContext, docId},
                });
                if (response.status === 200) {
                    form.validateFields().then(async data => {
                        if (setFormErrorMessage) {
                            setFormErrorMessage(null);
                        }

                        const requestData = {...data, docId};
                        const responseFile = await dispatch(
                            createActionGetFile({
                                referenceUrl,
                                requestType,
                                data: {},
                                isMessage: true,
                                linkedEntityName,
                                body: omitBy(requestData, v => v === '' || v === null),
                            }),
                        ) as unknown as AxiosResponse;
                        const blob = response?.data;
                        if (responseFile?.status === 200) {
                            dispatch(closeModal());
                        } else if (blob instanceof Blob && blob.type === 'text/plain' && setFormErrorMessage) {
                            setFormErrorMessage(await blob.text());
                        }
                    });
                }
            }
        } catch (e) {
            console.error(e);
            showMessageFromResponse({response: e.response, isError: true});
        }
    };

    return (
        <Button
            {...props}
            type="primary"
            style={{width: '100%'}}
            onClick={handleClick}
        >
            {actionIcon && <DynamicIcon type={actionIcon} />}
            {actionTitle}
        </Button>
    );
};
